/**
 * Taxonomia extensível e internacional.
 *
 * Princípio: as CATEGORIAS canónicas são poucas e genéricas; o que cresce são
 * os ALIASES (multilingues, singular/plural). Nada de enumerar o mundo.
 *
 * Qualquer nome próprio que não corresponda a uma categoria conhecida NÃO é
 * inventado nem forçado a uma categoria: é extraído como "item nomeado"
 * (obrigatório ou excluído) e validado mais tarde contra fontes reais.
 */

import type { ExcludedCategoryKind } from "../negative-preferences";

export type CanonicalCategory = ExcludedCategoryKind;

/**
 * Aliases por categoria. Escrevem-se em minúsculas e sem acentos; o plural
 * regular é derivado automaticamente (não é preciso escrever as duas formas).
 */
export const CATEGORY_ALIASES: Record<CanonicalCategory, string[]> = {
  restaurant: [
    "restaurante", "restaurant", "ristorante", "restaurante tipico", "tasca", "marisqueira",
    "bistro", "osteria", "trattoria", "eatery", "restaurante local",
    "comida", "food", "comer", "dining", "almoco", "jantar", "lunch", "dinner",
    "cena", "diner", "essen", "gastronomia", "gastronomy", "レストラン", "食事",
  ],
  cafe: ["cafe", "coffee shop", "cafeteria", "caffe", "kaffee", "salon de the", "カフェ"],
  bakery: ["pastelaria", "padaria", "confeitaria", "bakery", "patisserie", "boulangerie", "panaderia", "backerei", "パン屋"],
  museum: ["museu", "museum", "musee", "museo", "galeria de arte", "art gallery", "pinacoteca", "博物館", "美術館"],
  monument: ["monumento", "monument", "denkmal", "memorial", "記念碑"],
  church: ["igreja", "catedral", "cathedral", "church", "eglise", "iglesia", "kirche", "chiesa", "mosteiro", "abadia", "abbey", "templo", "temple", "寺", "神社"],
  park: ["parque", "jardim", "park", "garden", "jardin", "giardino", "garten", "公園"],
  nature: ["natureza", "trilho", "trail", "hike", "hiking", "floresta", "forest", "nature", "natur", "cascata", "waterfall", "自然"],
  shopping: ["compras", "loja", "shopping", "shop", "boutique", "einkaufen", "outlet", "買い物"],
  nightlife: ["bar", "discoteca", "club", "nightclub", "nightlife", "vida noturna", "nachtleben", "pub", "ナイトライフ"],
  market: ["mercado", "market", "marche", "mercato", "markt", "feira", "bazaar", "市場"],
  hotel: ["hotel", "alojamento", "accommodation", "hebergement", "unterkunft", "hostel", "pousada", "ryokan", "宿"],
  viewpoint: ["miradouro", "viewpoint", "belvedere", "mirador", "aussichtspunkt", "panorama", "展望台"],
  beach: ["praia", "beach", "plage", "playa", "spiaggia", "strand", "ビーチ", "海岸"],
  castle: ["castelo", "castle", "chateau", "castillo", "castello", "schloss", "burg", "fortaleza", "fort", "城"],
  zoo: ["zoo", "jardim zoologico", "zoologico", "aquario", "aquarium", "動物園", "水族館"],
  thermal: ["termas", "spa", "thermal", "onsen", "banhos", "baths", "therme", "温泉"],
  library: ["biblioteca", "library", "bibliotheque", "bibliothek", "図書館"],
  winery: ["adega", "vinha", "winery", "vineyard", "cave", "bodega", "cantina", "weingut", "ワイナリー"],
  theme_park: ["parque tematico", "theme park", "parc dattractions", "parque de diversiones", "freizeitpark", "遊園地"],
};

export const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/['’`]/gu, "")
    .toLowerCase()
    .trim();

/** Variantes singular/plural regulares para pt/es/fr/it/en/de. */
export function inflect(alias: string): string[] {
  const base = fold(alias);
  if (!base) return [];
  const out = new Set<string>([base]);
  if (/[aeiou]$/u.test(base)) out.add(`${base}s`);
  else if (/(?:r|z|s)$/u.test(base)) out.add(`${base}es`);
  else out.add(`${base}s`);
  if (base.endsWith("al")) out.add(`${base.slice(0, -2)}ais`);
  if (base.endsWith("y")) out.add(`${base.slice(0, -1)}ies`);
  // Plurais italianos regulares (a→e, o→i, e→i) e giа/cia→ge/ce.
  if (base.endsWith("gia") || base.endsWith("cia")) out.add(`${base.slice(0, -2)}e`);
  if (base.endsWith("a")) out.add(`${base.slice(0, -1)}e`);
  if (base.endsWith("o")) out.add(`${base.slice(0, -1)}i`);
  if (base.endsWith("e")) out.add(`${base.slice(0, -1)}i`);
  if (base.endsWith("s") && base.length > 4) out.add(base.slice(0, -1));
  if (base.endsWith("es") && base.length > 5) out.add(base.slice(0, -2));
  return [...out];
}

const ALIAS_INDEX: Map<string, CanonicalCategory> = (() => {
  const index = new Map<string, CanonicalCategory>();
  for (const [category, aliases] of Object.entries(CATEGORY_ALIASES) as Array<
    [CanonicalCategory, string[]]
  >) {
    for (const alias of aliases) {
      for (const variant of inflect(alias)) if (!index.has(variant)) index.set(variant, category);
    }
  }
  return index;
})();

/** Categoria canónica de um termo livre, ou null se não for reconhecido. */
export function resolveCategoryAlias(term: string): CanonicalCategory | null {
  const folded = fold(term);
  if (!folded) return null;
  return ALIAS_INDEX.get(folded) ?? null;
}

/** A janela de texto contém algum alias de categoria? Devolve todos. */
export function categoriesInText(text: string): CanonicalCategory[] {
  const folded = fold(text);
  if (!folded) return [];
  const words = folded.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  const found = new Set<CanonicalCategory>();
  for (let i = 0; i < words.length; i += 1) {
    for (let span = 3; span >= 1; span -= 1) {
      const phrase = words.slice(i, i + span).join(" ");
      const hit = ALIAS_INDEX.get(phrase);
      if (hit) {
        found.add(hit);
        break;
      }
    }
  }
  return [...found];
}

/** Aliases conhecidos (para sugestão administrativa e diagnóstico). */
export function knownAliases(category: CanonicalCategory): string[] {
  return CATEGORY_ALIASES[category] ?? [];
}

// ---------------------------------------------------------------------------
// Itens nomeados (nomes próprios que não são categorias)
// ---------------------------------------------------------------------------

export type NamedItem = {
  /** Nome tal como escrito pelo utilizador (preservado para pesquisa real). */
  name: string;
  /** Forma normalizada, usada em comparações e telemetria. */
  key: string;
  polarity: "required" | "excluded";
};

// Nota: o "no" isolado NÃO entra aqui — em pt/es é preposição ("no Algarve")
// e transformava destinos pedidos em exclusões.
const NEGATION_LEADS =
  /\b(?:sem|nao\s+quero|nao\s+queremos|dispenso|evita\w*|nada\s+de|without|dont\s+want|do\s+not\s+want|exclude|skip|avoid|sin|no\s+quiero|sans|pas\s+de|je\s+ne\s+veux\s+pas\s+de|senza|non\s+voglio|ohne|keine|vermeide\w*)\b/u;

const STOPWORDS = new Set([
  "de", "da", "do", "das", "dos", "e", "em", "no", "na", "nos", "nas", "a", "o", "os", "as",
  "the", "of", "and", "in", "at", "to", "el", "la", "los", "las", "y", "le", "les", "des", "du",
  "il", "lo", "gli", "der", "die", "das", "und", "quero", "queria", "gostava", "visitar", "ver",
  "want", "like", "visit", "see", "dia", "dias", "day", "days", "roteiro", "viagem", "trip",
]);

/**
 * Extrai nomes próprios (sequências capitalizadas ou entre aspas) que NÃO
 * correspondem a nenhuma categoria conhecida. A polaridade vem da frase em
 * que aparecem: se a oração tem uma negação antes do nome, é exclusão.
 *
 * Nunca se assume o que o nome é — só que existe e tem de ser validado.
 */
export function extractNamedItems(text: string): NamedItem[] {
  const raw = (text ?? "").trim();
  if (!raw) return [];
  const items = new Map<string, NamedItem>();

  for (const clause of raw.split(/[.;!?\n,]+/u)) {
    const folded = fold(clause);
    if (!folded) continue;
    const negIndexMatch = folded.match(NEGATION_LEADS);
    const negIndex = negIndexMatch?.index ?? -1;

    const candidates: Array<{ name: string; offset: number }> = [];
    const quoted = /["“”«»']([^"“”«»']{2,60})["“”«»']/gu;
    let q: RegExpExecArray | null;
    while ((q = quoted.exec(clause))) candidates.push({ name: q[1].trim(), offset: q.index });

    const proper = /\b(\p{Lu}[\p{L}\d'’-]+(?:\s+(?:de|do|da|dos|das|del|di|du|van|von|of|the|el|la)\s+\p{Lu}?[\p{L}\d'’-]+|\s+\p{Lu}[\p{L}\d'’-]+){0,3})/gu;
    let p: RegExpExecArray | null;
    while ((p = proper.exec(clause))) candidates.push({ name: p[1].trim(), offset: p.index });

    for (const candidate of candidates) {
      const key = fold(candidate.name);
      if (!key || key.length < 3) continue;
      if (STOPWORDS.has(key)) continue;
      if (resolveCategoryAlias(key)) continue; // é categoria, não item nomeado
      if (categoriesInText(key).length > 0 && key.split(" ").length === 1) continue;
      // Ignora a primeira palavra da frase quando é só capitalização inicial.
      if (candidate.offset === 0 && key.split(" ").length === 1 && !/["“«']/u.test(clause[0] ?? ""))
        continue;
      const foldedOffset = fold(clause.slice(0, candidate.offset)).length;
      const polarity: NamedItem["polarity"] =
        negIndex >= 0 && foldedOffset >= negIndex ? "excluded" : "required";
      const existing = items.get(key);
      if (!existing || (existing.polarity === "required" && polarity === "excluded")) {
        items.set(key, { name: candidate.name, key, polarity });
      }
    }
  }
  return [...items.values()];
}

/** Um item da lista corresponde a um nome excluído? (comparação tolerante) */
export function matchesNamedItem(text: string | null | undefined, key: string): boolean {
  if (!text || !key) return false;
  const haystack = fold(text);
  const needle = fold(key);
  if (!needle) return false;
  return haystack.includes(needle) || needle.includes(haystack);
}

/**
 * Aplica exclusões nomeadas a uma lista (pré-pesquisa e validação final).
 */
export function filterExcludedNamedItems<T>(
  items: readonly T[],
  excludedNames: readonly string[],
  textOf: (item: T) => string | null | undefined,
): T[] {
  if (excludedNames.length === 0) return items.slice();
  return items.filter((item) => !excludedNames.some((name) => matchesNamedItem(textOf(item), name)));
}

/** Verificação final: nenhum nome excluído pode aparecer no resultado. */
export function assertNoExcludedNamedItems<T>(
  items: readonly T[],
  excludedNames: readonly string[],
  textOf: (item: T) => string | null | undefined,
): void {
  for (const item of items) {
    for (const name of excludedNames) {
      if (matchesNamedItem(textOf(item), name)) {
        throw new Error(`assertNoExcludedNamedItems: "${name}" leaked into output`);
      }
    }
  }
}
